import {
  Avatar,
  CurrentUserDropdownButton,
  CurrentUserNavigation,
  ExternalIcon,
  LayerPortal,
  UserSectionDropdown,
} from '@yarmill/components';
import { Locale } from '@yarmill/types';
import {
  formatLocalizedLink,
  getAdjustedLocale,
  getUserName,
  useChangeLanguageHandler,
  useConfig,
  useCurrentUserStore,
  useHistory,
  useIntlStore,
  useLayer,
  useLocale,
  useLocation,
} from '@yarmill/utils';
import { observer } from 'mobx-react-lite';
import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAuthStore } from '../auth/hooks';
import { trackLogout } from '../google-analytics/utils';
import { ChangePassword } from '../profile/change-password';
import { AvatarLoader } from './avatar-loader';
import { UserSectionItem, UserSectionItemProps } from './user-section-item';
import {
  FloatingFocusManager,
  FloatingList,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react';

export const UserSection = observer(function UserSection(): ReactElement {
  const currentLang = useLocale();
  const authStore = useAuthStore();
  const externalLinks = useConfig('externalLinks');
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const currentUser = useCurrentUserStore();

  const changePasswordLayer = useLayer('full-screen');

  const availableLanguages = useConfig('availableLanguages');
  const handleChangeLanguage = useChangeLanguageHandler();
  const helpUrl = useConfig('helpUrl');
  const intlStore = useIntlStore();
  const elementsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { refs, floatingStyles, context } = useFloating({
    open: isDropdownOpened,
    onOpenChange: setIsDropdownOpened,
    placement: 'bottom-end',
  });

  const dismiss = useDismiss(context, {
    bubbles: true,
  });
  const click = useClick(context);
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    onNavigate: setActiveIndex,
    focusItemOnOpen: false,
  });
  const role = useRole(context, { role: 'menu' });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, click, role, listNavigation]
  );

  const changeLocale = useCallback(
    (lang: Locale): void => {
      handleChangeLanguage(lang);
    },
    [handleChangeLanguage]
  );

  const changePassword = useCallback((): void => {
    if (!changePasswordLayer.isOpened) {
      changePasswordLayer.open();
    }
  }, [changePasswordLayer]);

  const handleLogOut = useCallback((): void => {
    trackLogout();
    authStore.logOut().finally(() => (window.location.href = '/'));
  }, [authStore]);

  const closeDropdown = useCallback(() => {
    setIsDropdownOpened(false);
  }, []);

  const goToPersonal = useCallback((): void => {
    history.push('/settings/personal', {
      previousRoute: location,
    });
  }, [history, location]);

  const dropdownItems = useMemo(
    () =>
      [
        currentUser.data
          ? {
              dataTest: 'personal',
              onClick: goToPersonal,
              isHeader: true,
              children: getUserName(currentUser.data),
              key: 'header',
              ariaLabel: intl.formatMessage({
                id: 'header.navigation.goToUserProfile',
              }),
            }
          : null,
        ...(currentUser.data?.Instances.slice()
          .sort((a, b) =>
            a.Name.toLocaleLowerCase('cs').localeCompare(
              b.Name.toLocaleLowerCase('cs')
            )
          )
          .map(instance => ({
            dataTest: `sing-in-to-${instance.Ident}`,
            icon: <ExternalIcon name="CircleArrowRight" />,
            linkHref: instance.FeUrl,
            key: instance.Ident,
            children: intl.formatMessage(
              { id: 'header.navigation.signInTo' },
              { instance: instance.Name }
            ),
          })) ?? []),
        {
          icon: <ExternalIcon name="CirclePlus" />,
          linkHref: `https://yarmill.com/${
            currentLang === 'cs' ? 'cs/prihlasit' : 'en/sign-in'
          }`,
          dataTest: 'sing-in-to-other-team',
          children: intl.formatMessage({
            id: 'header.navigation.signInToOtherTeam',
          }),
          key: 'sign-in-to',
        },
        {
          key: 'change-password',
          dataTest: 'change-password',
          icon: <ExternalIcon name="Lock" />,
          onClick: changePassword,
          children: intl.formatMessage({
            id: 'header.navigation.changePassword',
          }),
        },
        helpUrl
          ? {
              key: 'help',
              dataTest: 'help',
              icon: <ExternalIcon name="Help" />,
              linkHref: formatLocalizedLink(
                helpUrl,
                getAdjustedLocale(intlStore.locale)
              ),
              linkTarget: '_blank',
              children: intl.formatMessage({ id: 'header.navigation.help' }),
            }
          : null,
        ...externalLinks.map(externalLink => ({
          key: `${externalLink.label}-${externalLink.url}`,
          dataTest: 'external-link',
          icon: <ExternalIcon name={externalLink.icon ?? 'FileCertificate'} />,

          linkHref: externalLink.url,
          linkTarget: '_blank',
          children: intl.formatMessage({ id: externalLink.label }),
        })),
        ...availableLanguages
          .filter(lang => lang !== currentLang)
          .map(lang => ({
            key: lang,
            dataTest: `set-language-${lang}`,
            icon: <ExternalIcon name="Language" />,
            onClick: () => changeLocale(lang),
            children: intl.formatMessage({
              id: `header.navigation.language.${lang}`,
            }),
          })),
        {
          key: 'log-out',
          dataTest: 'log-out',
          danger: true,
          onClick: handleLogOut,
          icon: <ExternalIcon name="Logout" />,
          children: intl.formatMessage({ id: 'header.navigation.logout' }),
        },
      ].filter(Boolean) as (UserSectionItemProps & { key: string })[],
    [
      availableLanguages,
      changeLocale,
      changePassword,
      currentLang,
      currentUser.data,
      externalLinks,
      handleLogOut,
      helpUrl,
      intl,
      intlStore.locale,
      goToPersonal,
    ]
  );

  return (
    <CurrentUserNavigation>
      <CurrentUserDropdownButton
        type="button"
        data-cy="user-dropdown-btn"
        ref={refs.setReference}
        aria-haspopup="true"
        aria-expanded={isDropdownOpened}
        aria-label={intl.formatMessage({ id: 'header.navigation.userMenu' })}
        {...getReferenceProps()}
      >
        {currentUser.data ? (
          <Avatar id={currentUser.data.AvatarFileName} disableFlex />
        ) : (
          <AvatarLoader />
        )}
      </CurrentUserDropdownButton>
      {isDropdownOpened && (
        <FloatingList elementsRef={elementsRef}>
          <FloatingPortal>
            <FloatingOverlay />
            <div
              style={{ ...floatingStyles, zIndex: 4, outline: 'none' }}
              {...getFloatingProps()}
              ref={refs.setFloating}
            >
              <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={1}
                returnFocus
              >
                <UserSectionDropdown>
                  {dropdownItems.map((item, idx) =>
                    item ? (
                      <UserSectionItem
                        {...item}
                        key={item.key}
                        isActive={idx === activeIndex}
                        getItemProps={getItemProps}
                        closeDropdown={closeDropdown}
                      />
                    ) : null
                  )}
                </UserSectionDropdown>
              </FloatingFocusManager>
            </div>
          </FloatingPortal>
        </FloatingList>
      )}
      <LayerPortal
        layerHandle={changePasswordLayer}
        getContent={layer => <ChangePassword layer={layer} />}
      />
    </CurrentUserNavigation>
  );
});
